import {
  AuthenticationResult,
  EventType,
  PublicClientApplication
} from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import DateFnsUtils from '@date-io/date-fns';
import { CssBaseline, useMediaQuery } from '@material-ui/core';
import { Theme, ThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { Chart as ChartJS, registerables } from 'chart.js';
import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { store } from './app/store';
import { ACTIVE_ACCOUNT_KEY } from './constants';
import './i18n';
import './index.css';
import { msalConfig } from './pages/Auth/authConfig';
import {
  getActiveAccount,
  silentSyncActiveAccount
} from './pages/Auth/authSCG';

ChartJS.register(...registerables);

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const instance = new PublicClientApplication(msalConfig);

// Initialize MSAL and sync account on load
async function initializeMsal() {
  const activeAccountKey = getActiveAccount();
  try {
    await instance.initialize();
    if (activeAccountKey)
      await silentSyncActiveAccount(instance, activeAccountKey);
  } catch (error) {
    console.error('Error initializing MSAL', error);
  }
}

// Handle MSAL events (login success, token success)
instance.addEventCallback((event) => {
  if (
    (event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
      event.eventType === EventType.SSO_SILENT_SUCCESS) &&
    isAuthenticationResult(event.payload) // Use a type guard
  ) {
    const account = event.payload.account;
    instance.setActiveAccount(account);
    localStorage.setItem(ACTIVE_ACCOUNT_KEY, account.homeAccountId);
  }
});

// Type guard function for authentication result
function isAuthenticationResult(payload: any): payload is AuthenticationResult {
  return payload && 'account' in payload;
}

// Initialize MSAL
initializeMsal();

declare module '@material-ui/core/styles/createPalette' {
  interface Palette {
    navbar: Palette['primary'];
    menu: Palette['primary'];
    gradient: Palette['primary'];
  }
  interface PaletteOptions {
    navbar: PaletteOptions['primary'];
    menu: PaletteOptions['primary'];
    gradient: PaletteOptions['primary'];
  }
}

export interface ColorModeContextType {
  toggleColorMode: () => void;
  selectColorMode: (theme: 'light' | 'dark' | 'system') => void;
  currentTheme: 'light' | 'dark' | 'system';
}

export const ColorModeContext = React.createContext<ColorModeContextType>({
  toggleColorMode: () => {},
  selectColorMode: (theme: 'light' | 'dark' | 'system') => {},
  currentTheme: 'light'
});

export function getCurrentTheme() {
  const theme = localStorage.getItem('theme');
  if (theme === 'light') return 'light';
  if (theme === 'dark') return 'dark';
  return 'system';
}

function AppWithTheme() {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [currentTheme, setCurrentTheme] = React.useState<
    'light' | 'dark' | 'system'
  >(getCurrentTheme());
  const [mode, setMode] = React.useState<'light' | 'dark'>(
    prefersDarkMode ? 'dark' : 'light'
  );

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => {
          const newMode = prevMode === 'light' ? 'dark' : 'light';
          localStorage.setItem('theme', newMode);
          return newMode;
        });
      },
      selectColorMode: (theme: 'light' | 'dark' | 'system') => {
        setCurrentTheme(theme);
        // No need to use setMode because when currentTheme was changed, mode will be changed at useEffect below.
        if (theme === 'system') {
          localStorage.removeItem('theme');
        } else {
          localStorage.setItem('theme', theme);
        }
      }
    }),
    []
  );

  const theme: Theme = createMuiTheme({
    palette: {
      type: mode,
      ...(mode === 'light'
        ? {
            // palette values for light mode
            primary: {
              main: '#1877f2',
              contrastText: '#ffffff'
            },
            secondary: {
              main: '#eb0014',
              contrastText: '#ffffff'
            },
            error: {
              main: '#fa3c3c',
              contrastText: '#ffffff'
            },
            success: {
              main: '#42b72a',
              contrastText: '#ffffff'
            },
            background: {
              default: '#f0f2f5'
            },
            text: {
              primary: '#1c1e21',
              disabled: '#dddddd'
            },
            navbar: {
              main: '#fff'
            },
            menu: {
              main: '#fff'
            },
            gradient: {
              main: 'linear-gradient(to right, #1877f2, #0059b2)'
            }
          }
        : {
            // palette values for dark mode
            primary: {
              main: '#007fff',
              contrastText: '#fafafa'
            },
            secondary: {
              main: '#eb0014',
              contrastText: '#fafafa'
            },
            error: {
              main: '#fa3c3c',
              contrastText: '#fafafa'
            },
            success: {
              main: '#42b72a',
              contrastText: '#fafafa'
            },
            background: {
              default: '#0d1117',
              paper: '#161b22'
            },
            text: {
              primary: '#fafafa',
              disabled: 'rgba(250, 250, 250, 0.5)'
            },
            navbar: {
              main: '#2b3036'
            },
            menu: {
              main: '#393d43'
            },
            gradient: {
              main: '#66b2ff'
            }
          })
    },
    typography: {
      fontFamily: 'Prompt'
    },
    overrides: {
      MuiButton: {
        root: {
          borderRadius: Number.MAX_VALUE
        }
      },
      MuiDialog: {
        root: {
          backdropFilter: 'blur(2px)'
        },
        paper: {
          borderRadius: '16px'
        }
      }
    }
  });

  React.useEffect(() => {
    if (currentTheme !== 'system') {
      setMode(currentTheme === 'light' ? 'light' : 'dark');
    } else {
      setMode(prefersDarkMode ? 'dark' : 'light');
    }
  }, [prefersDarkMode, currentTheme]);

  return (
    <ColorModeContext.Provider value={{ ...colorMode, currentTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <App />
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

ReactDOM.render(
  <BrowserRouter basename={process.env.PUBLIC_URL}>
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <MsalProvider instance={instance}>
        <Provider store={store}>
          <AppWithTheme />
        </Provider>
      </MsalProvider>
    </MuiPickersUtilsProvider>
  </BrowserRouter>,
  document.getElementById('root')
);
